import React from "react";
import { uniqueId } from "lodash";
import RichTextRender from "../../../../components/RichTextRender/RichTextRender";
import { useSelector } from "react-redux";
import { Collapse, List, Card } from "antd";
import Title from "antd/es/typography/Title";
import Text from "antd/es/typography/Text";
import { useTranslation } from "react-i18next";
import PromoItem from "../../../ShopPage/components/PromoItem/PromoItem";
import useIsMobile from "../../../../hooks/useIsMobile";
import { trackEvent } from "../../../../services/mixpanel";
import {
  getSupplierData,
  getSupplierSettings,
} from "../../../../Redux/Selectors/appConfigSelector";
import BundleDetails from "../../components/BundleDetails/BundleDetails";
import { getBundleById } from "../../../../Redux/Selectors/bundlesSelectors";
import Disclaimer from "../../../../components/Disclaimer/Disclaimer";
import { getProducts } from "../../../../Redux/Selectors/productSelectors";
import { getCategories } from "../../../../Redux/Selectors/categorySelectors";
import { mapCategoriesId, mapCategoryValues } from "../../helpers/helpers";
import ProductContentContainer from "../ProductContentContainer/ProductContentContainer";
import {
  getPassengersData,
  getActivePassenger,
} from "../../redux/QuoteSelectors";
import {
  ProductsContainer,
  Container,
  Content,
  Wrapper,
} from "./BundleContentContainer.styles";
import DropdownButton from "../../components/DropdownButton/DropdownButton";
import PriceContainer from "../PriceContainer/PriceContainer";
import { SKI_POLES } from "../../constants";

const BundleContentContainer = ({
  image,
  title,
  productId,
  description,
  passengerId,
  closeModal,
  subtitle,
  promo,
  category,
  products,
  productsId,
  visibleProductsByBundle,
}) => {
  const supplierData = useSelector(getSupplierData);
  const supplierSettings = useSelector(getSupplierSettings);
  const bundle = useSelector((state) => getBundleById(state, productId));
  const productsData = useSelector((state) => getProducts(state, productsId));
  const passengersData = useSelector(getPassengersData);
  const activePassengerId = useSelector(getActivePassenger);
  const categoryIds = mapCategoriesId(productsData);
  const categoryData = useSelector((state) =>
    getCategories(state, categoryIds)
  );
  const arrayCategories = mapCategoryValues(categoryData);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const rowId = uniqueId();

  const filteredProductsData = productsData.filter(
    (product) => !product.title.toLowerCase().includes(SKI_POLES)
  );

  const activePassenger =
    passengersData.find((p) => p.id === activePassengerId) || passengersData[0];

  const passengersMenu = {
    items: passengersData
      .filter((passenger) => passenger.id !== activePassengerId)
      .map((passenger) => ({
        key: passenger.id,
        label: t("shop:addToPassenger", {
          name: passenger.name || t("shop:unnamedPassenger"),
        }),
      })),
  };

  const renderPromos = () => {
    return (
      <List
        header={<Text strong>{t("shop:promotions.listHeader")}</Text>}
        footer={
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("shop:promotions.listFooter")}
          </Text>
        }
        dataSource={promo}
        renderItem={(item) => (
          <List.Item>
            <PromoItem
              key={productId}
              amount={item.amount}
              type={item.type}
              periodAmount={item.periodQuantity}
              operation={item.operation}
              periodType={bundle.periodType || "day"}
              currency={supplierSettings.currency}
              price={bundle.price}
            />
          </List.Item>
        )}
      />
    );
  };

  return (
    <Container>
      <Content>
        <Wrapper>
          <div>
            <img
              src={image}
              alt={t("shop:rentItemAlt", {
                title,
                supplierName: supplierData?.name,
              })}
              size="large"
              style={{
                height: isMobile ? 100 : 250,
                width: isMobile ? 100 : 250,
                minWidth: isMobile ? 100 : 250,
                objectFit: "contain",
              }}
              shape="square"
            />
          </div>
          <div>
            <Title
              type="secondary"
              level={5}
              style={{ marginTop: 0, fontSize: 14 }}
            >
              {subtitle}
            </Title>
            {description !== "null" && (
              <RichTextRender
                content={description}
                style={{
                  fontSize: 12,
                }}
              />
            )}
            <BundleDetails products={products} />
          </div>
        </Wrapper>
      </Content>
      <DropdownButton
        closeModal={closeModal}
        trackEvent={trackEvent}
        productId={productId}
        title={title}
        category={category}
        supplierData={supplierData}
        passengersMenu={passengersMenu}
        passengerId={passengerId}
        rowId={rowId}
        arrayCategories={arrayCategories}
        activePassenger={activePassenger}
        viewButtonAddPassenger={visibleProductsByBundle}
      />
      <Disclaimer
        text={t("shop:disclaimerImageProduct")}
        bordered={false}
        size="small"
      />
      {promo.length > 0 && (
        <Collapse style={{ width: "100%" }}>
          <Collapse.Panel header={t("shop:promotions.pickPromo")}>
            {renderPromos()}
          </Collapse.Panel>
        </Collapse>
      )}
      {visibleProductsByBundle &&
        filteredProductsData &&
        filteredProductsData.length > 0 && (
          <ProductsContainer>
            <Title level={5}>{t("shop:individual_products")}</Title>
            {filteredProductsData.map((product) => (
              <Card
                key={product._id}
                title={product.title}
                extra={<PriceContainer product={product} />}
              >
                <ProductContentContainer
                  id={product._id}
                  image={product.images?.[0]}
                  description={product.description}
                  subtitle={product.subtitle}
                  title={product.title}
                  passengerId={activePassengerId || passengerId}
                  closeModal={closeModal}
                  promo={product.promo || []}
                  price={product.price}
                  category={product.category?.value}
                  periodType={product.rentalPeriodType}
                  currency={product.currency}
                  passengersMenu={passengersMenu}
                  rowId={rowId}
                  arrayCategories={arrayCategories}
                  activePassenger={activePassenger}
                  viewButtonAddPassenger={visibleProductsByBundle}
                />
              </Card>
            ))}
          </ProductsContainer>
        )}
    </Container>
  );
};

export default BundleContentContainer;
